import { useState, useEffect, useMemo, type CSSProperties } from "react";
import { useEntityTypes, useEntities } from "../../../lib/hooks/use-kuvio";
import type { EntityTypeDefinition, EntityRecord } from "../../../lib/types";
import { useSessionScope, type ScopeRef } from "../../hooks/use-session-scope";
import { showToast } from "../primitives";

interface ScopePickerModalProps {
  open: boolean;
  onClose: () => void;
}

export function ScopePickerModal({ open, onClose }: ScopePickerModalProps) {
  const { scope, setScope } = useSessionScope();
  const entityTypes = useEntityTypes();
  const scopeTypes = useMemo(
    () => (entityTypes.data ?? []).filter((t) => t.is_scope),
    [entityTypes.data],
  );

  const [selected, setSelected] = useState<Map<string, ScopeRef>>(new Map());
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      const map = new Map<string, ScopeRef>();
      for (const ref of scope) map.set(ref.id, ref);
      setSelected(map);
      setSearch("");
    }
  }, [open, scope]);

  if (!open) return null;

  function toggle(ref: ScopeRef) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(ref.id)) next.delete(ref.id);
      else next.set(ref.id, ref);
      return next;
    });
  }

  function save() {
    setScope(Array.from(selected.values()));
    const count = selected.size;
    showToast(count > 0 ? `Scope set to ${count} ${count === 1 ? "entity" : "entities"}` : "Scope cleared");
    onClose();
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Set scope">
        <div style={header}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Set scope</h3>
          <button onClick={onClose} style={closeBtn} aria-label="Close">
            <i className="ti ti-x" style={{ fontSize: 16 }} />
          </button>
        </div>

        <div style={{ padding: "0 20px 12px" }}>
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entities…"
            style={searchInput}
          />
        </div>

        <div style={body}>
          {scopeTypes.length === 0 ? (
            <p style={emptyText}>
              No entity types have scope enabled. Enable "Use as scope dimension" on an entity type to start.
            </p>
          ) : (
            scopeTypes.map((t) => (
              <ScopeTypeSection
                key={t.slug}
                entityType={t}
                selected={selected}
                search={search}
                onToggle={toggle}
              />
            ))
          )}
        </div>

        <div style={footer}>
          <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>
            {selected.size} selected
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={cancelBtn}>Cancel</button>
            <button onClick={save} style={saveBtn}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function displayValue(r: EntityRecord, entityType: EntityTypeDefinition): string {
  const dispField = entityType.fields.find((f) => f.display_field)?.name;
  if (dispField) {
    const val = r[dispField];
    if (typeof val === "string" && val.length > 0) return val;
  }
  const nameVal = r["name"];
  if (typeof nameVal === "string" && nameVal.length > 0) return nameVal;
  return String(r.id ?? "");
}

function ScopeTypeSection({
  entityType,
  selected,
  search,
  onToggle,
}: {
  entityType: EntityTypeDefinition;
  selected: Map<string, ScopeRef>;
  search: string;
  onToggle: (ref: ScopeRef) => void;
}) {
  const entities = useEntities(entityType);
  const records = entities.data?.entities ?? [];

  const filtered = useMemo(() => {
    if (!search) return records;
    const q = search.toLowerCase();
    return records.filter((r) => {
      const display = displayValue(r, entityType).toLowerCase();
      return display.includes(q);
    });
  }, [records, search, entityType]);

  if (filtered.length === 0 && search) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={sectionHeader}>{entityType.name}</div>
      {entities.isLoading ? (
        <div style={loadingText}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={loadingText}>No records</div>
      ) : (
        filtered.map((r) => {
          const ref: ScopeRef = { kind: "entity", type: entityType.slug, id: r.id };
          const checked = selected.has(r.id);
          const label = displayValue(r, entityType);
          const showId = label !== String(r.id);
          return (
            <label key={r.id} style={rowStyle}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(ref)}
                style={{ accentColor: "var(--c-blue-600)" }}
              />
              <span style={{ fontSize: 13 }}>
                {label}
                {showId && (
                  <span style={{ marginLeft: 6, fontSize: 11, color: "var(--color-text-tertiary)" }}>
                    {String(r.id)}
                  </span>
                )}
              </span>
            </label>
          );
        })
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,0.35)",
};

const modal: CSSProperties = {
  width: 440,
  maxHeight: "70vh",
  display: "flex",
  flexDirection: "column",
  background: "var(--color-background-primary)",
  borderRadius: "var(--border-radius-lg)",
  boxShadow: "var(--shadow-xl)",
  fontFamily: "var(--font-sans)",
  overflow: "hidden",
};

const header: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px 8px",
};

const closeBtn: CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  padding: 4,
};

const searchInput: CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 13,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-secondary)",
  color: "var(--color-text-primary)",
  outline: "none",
  fontFamily: "var(--font-sans)",
};

const body: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "0 20px",
  minHeight: 120,
};

const emptyText: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-tertiary)",
  textAlign: "center",
  padding: "24px 0",
};

const sectionHeader: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-tertiary)",
  marginBottom: 6,
};

const loadingText: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  padding: "4px 0",
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "5px 0",
  cursor: "pointer",
};

const footer: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 20px",
  borderTop: "var(--kuvio-border-default)",
};

const cancelBtn: CSSProperties = {
  fontSize: 13,
  padding: "6px 14px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const saveBtn: CSSProperties = {
  fontSize: 13,
  padding: "6px 14px",
  border: "none",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--c-blue-600)",
  color: "#fff",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
};
